// src/utils/charts/dataUtils.ts
/**
 * Chart Data Utilities
 * 
 * Centralized functions for parsing, aligning, and transforming time series data
 * for use with ECharts visualizations.
 */

// ============================================================================
// DATE PARSING
// ============================================================================

/**
 * Parse various date formats into Unix timestamp (ms)
 * Handles: YYYYMMDD integers, ISO strings, Unix timestamps (s or ms)
 */
export function parseDateToTimestamp(date: string | number | Date): number {
  if (date instanceof Date) {
    return date.getTime();
  }
  
  if (typeof date === 'number') {
    // YYYYMMDD integer format (e.g., 20240115)
    if (date >= 10000101 && date <= 99991231) {
      const y = Math.floor(date / 10000);
      const m = Math.floor((date % 10000) / 100);
      const d = date % 100;
      return Date.UTC(y, m - 1, d);
    }
    // Unix timestamp in seconds (< 1e12) or milliseconds
    return date < 1e12 ? date * 1000 : date;
  }
  
  // ISO string or other parseable format
  const parsed = Date.parse(date);
  return Number.isFinite(parsed) ? parsed : 0;
}

/**
 * Convert YYYYMMDD integer to ISO date string (YYYY-MM-DD)
 */
export function intDateToIso(dateInt: number): string {
  const y = Math.floor(dateInt / 10000);
  const m = Math.floor((dateInt % 10000) / 100);
  const d = dateInt % 100;
  return new Date(Date.UTC(y, m - 1, d)).toISOString().slice(0, 10);
}

/**
 * Format timestamp to display label
 * @param format - 'short' (Jan 15), 'medium' (Jan 15, 2024), 'long' (Mon, Jan 15, 2024), 'monthYear' (Jan 2024)
 */
export function formatDateLabel(
  timestamp: number | string | Date,
  format: 'short' | 'medium' | 'long' | 'monthYear' = 'medium'
): string {
  const ts = typeof timestamp === 'number' 
    ? timestamp 
    : parseDateToTimestamp(timestamp);
  
  const date = new Date(ts);
  
  switch (format) {
    case 'short':
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    case 'medium':
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    case 'long':
      return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
    case 'monthYear':
      return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
    default:
      return date.toISOString().slice(0, 10);
  }
}

/**
 * Format date for axis label (MM-DD)
 */
export function formatAxisDate(timestamp: number): string {
  const d = new Date(timestamp);
  const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(d.getUTCDate()).padStart(2, '0');
  return `${mm}-${dd}`;
}

// ============================================================================
// SERIES ALIGNMENT
// ============================================================================

export interface TimeSeriesPoint {
  date: number; // timestamp in ms
  value: number;
}

export interface RawTimeSeries {
  dates: (string | number)[];
  values: (number | null)[];
}

/**
 * Convert raw API response to normalized time series points
 */
export function normalizeTimeSeries(raw: RawTimeSeries): TimeSeriesPoint[] {
  const points: TimeSeriesPoint[] = [];
  const { dates, values } = raw;
  
  for (let i = 0; i < dates.length; i++) {
    const date = parseDateToTimestamp(dates[i]!);
    const value = values[i];
    
    if (date > 0 && value !== null && Number.isFinite(value)) {
      points.push({ date, value: Number(value) });
    }
  }
  
  return points.sort((a, b) => a.date - b.date);
}

/**
 * Build union of all dates across multiple series
 */
export function buildDateUnion(seriesList: TimeSeriesPoint[][]): number[] {
  const dateSet = new Set<number>();
  
  for (const series of seriesList) {
    for (const point of series) {
      dateSet.add(point.date);
    }
  }
  
  return Array.from(dateSet).sort((a, b) => a - b);
}

/**
 * Align a series to a common date axis with forward-fill
 */
export function alignToDateAxis(
  series: TimeSeriesPoint[],
  dates: number[],
  fillMethod: 'forward' | 'null' | 'zero' = 'forward'
): (number | null)[] {
  const dateMap = new Map<number, number>();
  series.forEach(pt => dateMap.set(pt.date, pt.value));
  
  const aligned: (number | null)[] = [];
  let lastValue: number | null = null;
  
  for (const date of dates) {
    const value = dateMap.get(date);
    
    if (value !== undefined) {
      lastValue = value;
      aligned.push(value);
    } else {
      switch (fillMethod) {
        case 'forward':
          aligned.push(lastValue);
          break;
        case 'zero':
          aligned.push(0);
          break;
        default:
          aligned.push(null);
      }
    }
  }
  
  return aligned;
}

// ============================================================================
// RANGE SLICING
// ============================================================================

export type TimeRange = '7d' | '30d' | '90d' | '180d' | '365d' | 'All';

const RANGE_TO_DAYS: Record<TimeRange, number> = {
  '7d': 7,
  '30d': 30,
  '90d': 90,
  '180d': 180,
  '365d': 365,
  'All': Infinity,
};

/**
 * Slice data to specified time range
 */
export function sliceByRange<T>(data: T[], range: TimeRange): T[] {
  const days = RANGE_TO_DAYS[range];
  if (days === Infinity || days >= data.length) {
    return data;
  }
  return data.slice(-days);
}

/**
 * Slice dates and values together by range
 */
export function sliceSeriesByRange(
  dates: number[],
  values: (number | null)[],
  range: TimeRange
): { dates: number[]; values: (number | null)[] } {
  const days = RANGE_TO_DAYS[range];
  if (days === Infinity || days >= dates.length) {
    return { dates, values };
  }
  return {
    dates: dates.slice(-days),
    values: values.slice(-days),
  };
}

/**
 * Find first index with valid (finite) data
 */
export function findFirstValidIndex(values: (number | null)[]): number {
  return values.findIndex(v => v !== null && Number.isFinite(v));
}

/**
 * Trim leading invalid values from series
 */
export function trimLeadingInvalid(
  dates: number[],
  values: (number | null)[]
): { dates: number[]; values: (number | null)[] } {
  const firstValid = findFirstValidIndex(values);
  if (firstValid < 0) {
    return { dates: [], values: [] };
  }
  return {
    dates: dates.slice(firstValid),
    values: values.slice(firstValid),
  };
}

// ============================================================================
// RETURNS CALCULATIONS
// ============================================================================

/**
 * Convert price series to daily returns
 */
export function toReturns(prices: number[]): number[] {
  const returns: number[] = [];
  
  for (let i = 1; i < prices.length; i++) {
    const prev = prices[i - 1]!;
    const curr = prices[i]!;
    
    if (prev > 0 && Number.isFinite(prev) && Number.isFinite(curr)) {
      returns.push((curr / prev) - 1);
    } else {
      returns.push(NaN);
    }
  }
  
  return returns;
}

/**
 * Convert returns to cumulative indexed series (starting at 100)
 */
export function returnsToCumulative(returns: number[], startValue: number = 100): number[] {
  const cumulative: number[] = [startValue];
  let current = startValue;
  
  for (const r of returns) {
    if (Number.isFinite(r)) {
      current *= (1 + r);
    }
    cumulative.push(current);
  }
  
  return cumulative;
}

/**
 * Compute drawdown series from prices
 */
export function computeDrawdown(prices: number[]): { values: number[]; maxDd: number; currentDd: number | null } {
  const dd: number[] = [];
  let peak = -Infinity;
  let maxDd = 0;
  
  for (const p of prices) {
    if (!Number.isFinite(p)) {
      dd.push(NaN);
      continue;
    }
    peak = Math.max(peak, p);
    const drawdown = peak > 0 ? ((p / peak) - 1) * 100 : 0;
    dd.push(drawdown);
    maxDd = Math.min(maxDd, drawdown);
  }
  
  const last = dd.length > 0 ? dd[dd.length - 1] : null;
  
  return {
    values: dd,
    maxDd,
    currentDd: last !== undefined && Number.isFinite(last) ? last : null,
  };
}

// ============================================================================
// STATISTICAL HELPERS
// ============================================================================

/**
 * Compute rolling mean
 */
export function rollingMean(values: number[], window: number): number[] {
  const result: number[] = new Array(values.length).fill(NaN);
  let sum = 0;
  let count = 0;
  
  for (let i = 0; i < values.length; i++) {
    const v = values[i]!;
    
    if (Number.isFinite(v)) {
      sum += v;
      count++;
    }
    
    if (i >= window) {
      const old = values[i - window]!;
      if (Number.isFinite(old)) {
        sum -= old;
        count--;
      }
    }
    
    if (i >= window - 1 && count > 0) {
      result[i] = sum / count;
    }
  }
  
  return result;
}

/**
 * Compute exponential moving average
 */
export function ema(values: number[], span: number): number[] {
  const alpha = 2 / (span + 1);
  const result: number[] = new Array(values.length).fill(NaN);
  let prev = NaN;
  
  for (let i = 0; i < values.length; i++) {
    const v = values[i]!;
    
    if (!Number.isFinite(v)) {
      result[i] = prev;
      continue;
    }
    
    if (!Number.isFinite(prev)) {
      prev = v;
    } else {
      prev = alpha * v + (1 - alpha) * prev;
    }
    
    result[i] = prev;
  }
  
  return result;
}

/**
 * Compute z-scores for a series
 */
export function zScore(values: number[]): number[] {
  const valid = values.filter(v => Number.isFinite(v));
  if (valid.length === 0) return values.map(() => NaN);
  
  const mean = valid.reduce((a, b) => a + b, 0) / valid.length;
  const variance = valid.reduce((a, v) => a + Math.pow(v - mean, 2), 0) / valid.length;
  const std = Math.sqrt(variance) || 1;
  
  return values.map(v => Number.isFinite(v) ? (v - mean) / std : NaN);
}

/**
 * Compute mean and standard deviation bands
 */
export function computeMeanBands(values: number[]): { mean: number; upper: number; lower: number } {
  const valid = values.filter(v => Number.isFinite(v));
  if (valid.length === 0) {
    return { mean: NaN, upper: NaN, lower: NaN };
  }
  
  const mean = valid.reduce((a, b) => a + b, 0) / valid.length;
  const variance = valid.reduce((a, v) => a + Math.pow(v - mean, 2), 0) / (valid.length - 1);
  const std = Math.sqrt(variance);
  
  return {
    mean,
    upper: mean + std,
    lower: Math.max(0, mean - std),
  };
}

// ============================================================================
// RESAMPLE FUNCTIONS
// ============================================================================

/**
 * Resample daily data to weekly (taking last value of each week)
 */
export function resampleWeekly(
  dates: number[],
  values: (number | null)[]
): { dates: number[]; values: (number | null)[] } {
  if (dates.length === 0) return { dates: [], values: [] };
  
  const result: { dates: number[]; values: (number | null)[] } = { dates: [], values: [] };
  let lastWeek = -1;
  
  for (let i = 0; i < dates.length; i++) {
    const date = new Date(dates[i]!);
    // Get ISO week number
    const startOfYear = new Date(Date.UTC(date.getUTCFullYear(), 0, 1));
    const weekNum = Math.ceil(((date.getTime() - startOfYear.getTime()) / 86400000 + startOfYear.getUTCDay() + 1) / 7);
    const yearWeek = date.getUTCFullYear() * 100 + weekNum;
    
    if (yearWeek !== lastWeek) {
      result.dates.push(dates[i]!);
      result.values.push(values[i]!);
      lastWeek = yearWeek;
    } else {
      // Update to latest value in the week
      result.dates[result.dates.length - 1] = dates[i]!;
      result.values[result.values.length - 1] = values[i]!;
    }
  }
  
  return result;
}

// ============================================================================
// VALIDATION HELPERS
// ============================================================================

/**
 * Check if series has enough valid data points
 */
export function hasMinimumData(values: (number | null)[], minPoints: number = 2): boolean {
  const validCount = values.filter(v => v !== null && Number.isFinite(v)).length;
  return validCount >= minPoints;
}

/**
 * Get count of valid (non-null, finite) values
 */
export function countValidValues(values: (number | null)[]): number {
  return values.filter(v => v !== null && Number.isFinite(v)).length;
}

/**
 * Filter out invalid values while preserving indices
 */
export function filterValid(values: number[]): { values: number[]; indices: number[] } {
  const result: { values: number[]; indices: number[] } = { values: [], indices: [] };
  
  for (let i = 0; i < values.length; i++) {
    const v = values[i]!;
    if (Number.isFinite(v)) {
      result.values.push(v);
      result.indices.push(i);
    }
  }
  
  return result;
}

